/*
  FC Bundles — behaviour for the "Bundel en bespaar" product carousel.
  Scroll-snap track with dots, prev/next arrows, and fading of partially
  visible cards. Quick add is handled by the theme's own <product-form>
  element (global.js) — nothing cart-related lives here.

  The track loops infinitely: once scrolling settles, cards are recycled from
  one end of the track to the other and scrollLeft is compensated by the same
  amount, so the cards on screen never move but there is always another card
  in both directions. Recycling (rather than cloning) keeps every product in
  the DOM exactly once — no duplicate form ids, no aria-hidden interactive
  copies, nothing for a screen reader to read twice.

  Loaded by sections/main-product.liquid only when the block is present.
  Developer: Alfie
  Updated:   2026-09-10
*/

if (!customElements.get('fc-bundles')) {
  customElements.define('fc-bundles', class FcBundles extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.fc-bundles__track');
      this.slides = Array.from(this.querySelectorAll('.fc-bundles__card'));
      this.dotsWrap = this.querySelector('[data-fc-bundles-dots]');
      this.prevButton = this.querySelector('[name="previous"]');
      this.nextButton = this.querySelector('[name="next"]');
      if (!this.track || this.slides.length === 0) return;

      // Collection index of the card currently sitting first in the DOM.
      // Recycling drifts DOM order away from collection order, so the dots
      // need this to stay pointed at the right card.
      this.origin = 0;

      this.track.addEventListener('scroll', this.onScroll.bind(this), { passive: true });
      // Moving a card unfocuses anything inside it, so normalise() leaves the
      // track alone while a keyboard user is in it — pick it back up on exit.
      this.track.addEventListener('focusout', this.scheduleNormalise.bind(this));
      if (this.prevButton) this.prevButton.addEventListener('click', this.scrollBySlide.bind(this, -1));
      if (this.nextButton) this.nextButton.addEventListener('click', this.scrollBySlide.bind(this, 1));

      // Partially visible cards render at 20% opacity (Figma) — observed
      // against the track so it self-updates while scrolling
      // Any card that isn't (essentially) fully inside the scrollport fades —
      // the Figma fades the peeking card no matter how much of it shows.
      // 0.95 rather than 1 tolerates subpixel rounding on fully visible cards.
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          entry.target.classList.toggle('is-faded', entry.intersectionRatio < 0.95);
        });
      }, { root: this.track, threshold: [0, 0.95, 1] });
      this.slides.forEach((slide) => this.observer.observe(slide));

      this.measure();
      window.addEventListener('resize', this.measure.bind(this));
    }

    // Bleed, loop slack and dot count all depend on the track's width, so they
    // are measured together on load and on every resize.
    measure() {
      this.setBleed();
      this.normalise();
      this.renderDots();
      this.update();
    }

    // Desktop bleed, responsive to the real screen width. Two cases:
    // - the screen fits one extra whole card (e.g. 3 full): end the
    //   scrollport exactly on its edge — no cut-off card, whitespace after
    // - otherwise: the peeking (faded) card is cut by the screen edge itself
    // documentElement.clientWidth excludes the scrollbar, so the bleed never
    // causes horizontal page overflow. At the design's 1440 base the screen
    // edge and the container edge coincide, matching the Figma.
    setBleed() {
      const docWidth = document.documentElement.clientWidth;
      const rect = this.getBoundingClientRect();
      const viewportBleed = Math.max(0, docWidth - rect.right);

      const pitch = this.slideOffset();
      const gap = pitch - this.track.firstElementChild.offsetWidth;
      const baseCards = Math.floor((rect.width + gap) / pitch);
      const snapBleed = baseCards + 1 > this.slides.length
        ? Infinity
        : (baseCards + 1) * pitch - gap - rect.width;

      const bleed = snapBleed <= viewportBleed ? snapBleed : viewportBleed;
      this.style.setProperty('--fc-bundles-bleed', `${bleed}px`);
    }

    // Every card is the same width with a uniform gap, so one pitch describes
    // the whole track. Measured from DOM-adjacent cards, never from
    // this.slides — recycling reorders the DOM but not that array.
    slideOffset() {
      const first = this.track.firstElementChild;
      if (this.slides.length < 2) return first.offsetWidth;
      return first.nextElementSibling.offsetLeft - first.offsetLeft;
    }

    maxScrollLeft() {
      return this.track.scrollWidth - this.track.clientWidth;
    }

    // Looping needs two cards of overflow: one for the slack it parks to the
    // left of the scrollport, one so compensating a recycle can never be
    // clamped by the end of the track (which would show as a jump). Below
    // that — a screen wide enough to show nearly every card — the carousel
    // falls back to wrapping round to the far end instead.
    canLoop() {
      return this.maxScrollLeft() > this.slideOffset() * 2 + 1;
    }

    // Number of scroll positions. While looping every card can lead the
    // scrollport, so there is one per card. Without the loop it is NOT the
    // number of cards: the track stops scrolling with the last few already on
    // screen. Either way the count changes with the viewport width.
    // -1 tolerates fractional scroll widths.
    pageCount() {
      const maxScrollLeft = this.maxScrollLeft();
      if (maxScrollLeft <= 1) return 1;
      if (this.canLoop()) return this.slides.length;
      return Math.min(this.slides.length, Math.ceil((maxScrollLeft - 1) / this.slideOffset()) + 1);
    }

    currentIndex() {
      if (this.canLoop()) {
        // The card this far into the DOM order leads the scrollport; origin
        // translates that back into a collection index.
        const offset = Math.round(this.track.scrollLeft / this.slideOffset());
        return (this.origin + offset) % this.slides.length;
      }

      const lastIndex = this.pageCount() - 1;
      // At the end of the track scrollLeft never reaches lastIndex * pitch
      // (the remaining cards are already on screen), so snap to the last
      // position — otherwise the final dot never activates.
      if (this.track.scrollLeft >= this.maxScrollLeft() - 1) return lastIndex;
      return Math.min(Math.round(this.track.scrollLeft / this.slideOffset()), lastIndex);
    }

    // One dot per scroll position; rebuilt only when that count changes
    renderDots() {
      if (!this.dotsWrap) return;
      const count = this.pageCount();
      if (this.dots && this.dots.length === count) return;
      this.dotsWrap.textContent = '';
      this.dots = Array.from({ length: count }, () => {
        const dot = document.createElement('span');
        dot.className = 'fc-bundles__dot';
        this.dotsWrap.appendChild(dot);
        return dot;
      });
    }

    // Moves the card at one end of the track to the other and compensates
    // scrollLeft by the same amount: the cards on screen stay exactly where
    // they are, the track just gains room to carry on in that direction.
    // 'instant' because the track's CSS scroll-behavior is smooth — an
    // animated compensation would be visible as a jump.
    recycleForward() {
      const pitch = this.slideOffset();
      const left = this.track.scrollLeft;
      this.track.appendChild(this.track.firstElementChild);
      this.origin = (this.origin + 1) % this.slides.length;
      this.track.scrollTo({ left: left - pitch, behavior: 'instant' });
      return pitch;
    }

    recycleBackward() {
      const pitch = this.slideOffset();
      const left = this.track.scrollLeft;
      this.track.prepend(this.track.lastElementChild);
      this.origin = (this.origin - 1 + this.slides.length) % this.slides.length;
      this.track.scrollTo({ left: left + pitch, behavior: 'instant' });
      return pitch;
    }

    // Parks the track one card in from the left, so there is always a card to
    // reveal in either direction. Runs once scrolling has settled: recycling
    // mid-gesture, or mid smooth-scroll, would cancel the browser's animation.
    normalise() {
      if (!this.canLoop()) return;
      // Recycling a card that holds focus would drop that focus to the body.
      if (this.track.contains(document.activeElement)) return;

      const pitch = this.slideOffset();
      // Counted up front rather than looped against a live scrollLeft, so a
      // clamped scroll position can never spin this.
      const steps = Math.round((this.track.scrollLeft - pitch) / pitch);
      for (let i = 0; i < steps; i += 1) this.recycleForward();
      for (let i = 0; i > steps; i -= 1) this.recycleBackward();
    }

    scheduleNormalise() {
      clearTimeout(this.settleTimer);
      this.settleTimer = setTimeout(this.normalise.bind(this), 120);
    }

    // Steps one card, recycling first when the step would run past either end
    // of the track — so the arrows never dead-end and the browser still
    // animates a plain one-card scroll.
    scrollBySlide(direction) {
      const pitch = this.slideOffset();
      let target = this.track.scrollLeft + direction * pitch;

      if (this.canLoop()) {
        const maxScrollLeft = this.maxScrollLeft();
        while (target < 0) target += this.recycleBackward();
        while (target > maxScrollLeft) target -= this.recycleForward();
      } else {
        // Too few cards to recycle: wrap round to the far end instead.
        const count = this.pageCount();
        const index = (this.currentIndex() + direction + count) % count;
        target = Math.min(index * pitch, this.maxScrollLeft());
      }

      this.track.scrollTo({ left: target });
    }

    onScroll() {
      window.requestAnimationFrame(this.update.bind(this));
      this.scheduleNormalise();
    }

    update() {
      const index = this.currentIndex();
      if (this.dots) this.dots.forEach((dot, i) => dot.classList.toggle('is-active', i === index));

      // The carousel loops, so the arrows stay live at both ends — they only
      // go dead when every card already fits on screen.
      const isStatic = this.pageCount() === 1;
      if (this.prevButton) this.prevButton.toggleAttribute('disabled', isStatic);
      if (this.nextButton) this.nextButton.toggleAttribute('disabled', isStatic);
    }
  });
}
